import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface ForumCategory {
    id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    is_active: boolean;
    sort_order: number;
    requires_approval: boolean;
    allowed_roles: string[];
    topic_count: number;
    latest_topic_title?: string;
    latest_topic_date?: string;
    created_at: string;
}

interface ForumTopic {
    id: string;
    title: string;
    content: string;
    category_name: string;
    author_name: string;
    is_pinned: boolean;
    is_locked: boolean;
    reply_count: number;
    view_count: number;
    last_reply_date?: string;
    last_reply_author?: string;
    tags: string[];
    created_at: string;
}

interface Props {
    onBack: () => void;
}

const ForumCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [view, setView] = useState<'categories' | 'topics'>('categories');
    const [selectedCategory, setSelectedCategory] = useState<ForumCategory | null>(null);
    
    // Categories state
    const [categories, setCategories] = useState<ForumCategory[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
    
    // Topics state
    const [topics, setTopics] = useState<ForumTopic[]>([]);
    const [topicsLoading, setTopicsLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(15);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (view === 'topics' && selectedCategory) {
            loadTopics();
        }
    }, [view, selectedCategory]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || topicsLoading || !hasMore) {
                return;
            }
            if (view === 'topics') {
                loadMoreTopics();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [view, topicsLoading, hasMore]);

    const loadCategories = async () => {
        setCategoriesLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/forum-categories/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    setCategories(response.data.results);
                } else if (Array.isArray(response.data)) {
                    setCategories(response.data);
                }
            }
        } catch (error) {
            console.error('Error loading forum categories:', error);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const loadTopics = async () => {
        if (!selectedCategory) return;
        
        setTopicsLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/forum-topics/`;
            const params = new URLSearchParams();
            params.append('category', selectedCategory.id);
            params.append('page_size', itemsPerPage.toString());

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    setTopics(response.data.results);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    setTopics(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error loading forum topics:', error);
            setTopics([]);
        } finally {
            setTopicsLoading(false);
        }
    };

    const loadMoreTopics = async () => {
        if (topicsLoading || !hasMore || !selectedCategory) return;

        setTopicsLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/forum-topics/`;
            const params = new URLSearchParams();
            params.append('category', selectedCategory.id);
            params.append('page_size', itemsPerPage.toString());
            params.append('page', (currentPage + 1).toString());

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data.results)) {
                setTopics(prev => [...prev, ...response.data.results]);
                setHasMore(!!response.data.next);
                setCurrentPage(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error loading more topics:', error);
        } finally {
            setTopicsLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatRelativeTime = (dateString: string) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffDays > 7) {
            return formatDate(dateString);
        } else if (diffDays > 0) {
            return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        } else if (diffMinutes > 0) {
            return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    const openCategory = (category: ForumCategory) => {
        setSelectedCategory(category);
        setView('topics');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const backToCategories = () => {
        setView('categories');
        setSelectedCategory(null);
        setTopics([]);
        setSearchTerm('');
    };

    const viewTopic = (topic: ForumTopic) => {
        Swal.fire({
            title: topic.title,
            html: `
                <div class="text-start">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <span class="badge bg-primary">${topic.category_name}</span>
                            ${topic.is_pinned ? '<span class="badge bg-warning ms-1">Pinned</span>' : ''}
                            ${topic.is_locked ? '<span class="badge bg-secondary ms-1">Locked</span>' : ''}
                        </div>
                        <small class="text-muted">${formatDate(topic.created_at)}</small>
                    </div>
                    
                    <div class="mb-3">
                        <strong>Author:</strong> ${topic.author_name}
                    </div>
                    
                    ${topic.tags && topic.tags.length > 0 ? `
                        <div class="mb-3">
                            <strong>Tags:</strong> 
                            ${topic.tags.map(tag => `<span class="badge bg-light text-dark ms-1">${tag}</span>`).join('')}
                        </div>
                    ` : ''}
                    
                    <div class="mb-3">
                        <small class="text-muted">
                            <i class="bx bx-message me-1"></i>${topic.reply_count} replies
                            <i class="bx bx-show ms-3 me-1"></i>${topic.view_count} views
                        </small>
                    </div>
                    
                    ${topic.last_reply_date ? `
                        <div class="mb-3">
                            <small class="text-muted">
                                <strong>Last reply:</strong> ${formatRelativeTime(topic.last_reply_date)} by ${topic.last_reply_author}
                            </small>
                        </div>
                    ` : ''}
                    
                    <hr>
                    <div>${topic.content}</div>
                </div>
            `,
            width: '700px',
            showCloseButton: true,
            confirmButtonText: 'Close',
            customClass: {
                htmlContainer: 'text-start'
            }
        });
    };

    const createNewTopic = () => {
        if (!selectedCategory) return;

        Swal.fire({
            title: 'Create New Topic',
            html: `
                <div class="text-start">
                    <div class="mb-3">
                        <label class="form-label">Topic Title</label>
                        <input type="text" id="topicTitle" class="form-control" placeholder="Enter topic title">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Content</label>
                        <textarea id="topicContent" class="form-control" rows="6" placeholder="Write your topic content..."></textarea>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Tags (optional)</label>
                        <input type="text" id="topicTags" class="form-control" placeholder="Enter tags separated by commas">
                    </div>
                </div>
            `,
            width: '600px',
            showCancelButton: true,
            confirmButtonText: 'Create Topic',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const title = (document.getElementById('topicTitle') as HTMLInputElement)?.value;
                const content = (document.getElementById('topicContent') as HTMLTextAreaElement)?.value;
                const tags = (document.getElementById('topicTags') as HTMLInputElement)?.value;

                if (!title || !content) {
                    Swal.showValidationMessage('Please fill in title and content');
                    return false;
                }

                return { title, content, tags };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Here you would make an API call to create the topic
                Swal.fire('Success!', 'Your topic has been created successfully.', 'success')
                    .then(() => loadTopics());
            }
        });
    };

    const filteredTopics = topics.filter(topic =>
        topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.author_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderCategories = () => (
        <div className="row">
            {categories.map(category => (
                <div key={category.id} className="col-lg-6 col-xl-4 mb-4">
                    <div 
                        className="card h-100 forum-category-card"
                        onClick={() => openCategory(category)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="card-body">
                            <div className="d-flex align-items-start mb-3">
                                <div 
                                    className="forum-icon me-3"
                                    style={{ backgroundColor: category.color }}
                                >
                                    <i className={`${category.icon} text-white`}></i>
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="card-title mb-1">{category.name}</h6>
                                    <p className="card-text text-muted small mb-0">
                                        {category.description}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    <i className="bx bx-message me-1"></i>
                                    {category.topic_count || 0} topics
                                </small>
                                {category.requires_approval && (
                                    <small className="text-warning">
                                        <i className="bx bx-shield me-1"></i>
                                        Moderated
                                    </small>
                                )}
                            </div>
                            
                            {category.latest_topic_title && (
                                <div className="mt-2 pt-2 border-top">
                                    <small className="text-muted">
                                        <strong>Latest:</strong> {category.latest_topic_title}
                                        <br />
                                        <small>{formatRelativeTime(category.latest_topic_date!)}</small>
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderTopics = () => (
        <>
            {/* Topics Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <button 
                                className="btn btn-outline-secondary btn-sm me-3"
                                onClick={backToCategories}
                            >
                                <i className="bx bx-arrow-back me-1"></i>
                                Back to Categories
                            </button>
                            <span className="text-muted">
                                <i className={`${selectedCategory?.icon} me-1`} style={{ color: selectedCategory?.color }}></i>
                                {selectedCategory?.name}
                            </span>
                        </div>
                        <button 
                            className="btn btn-primary btn-sm"
                            onClick={createNewTopic}
                            disabled={!selectedCategory?.is_active}
                        >
                            <i className="bx bx-plus me-1"></i>
                            New Topic
                        </button>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="position-relative">
                        <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            className="form-control ps-5"
                            placeholder="Search topics..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <span className="text-muted">
                        Showing {filteredTopics.length} of {totalCount} topics
                    </span>
                </div>
            </div>

            {/* Topics List */}
            <div className="row">
                <div className="col-12">
                    {filteredTopics.map(topic => (
                        <div key={topic.id} className="card mb-3 topic-card">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-2">
                                                    <h6 
                                                        className="mb-0 me-2 topic-title"
                                                        onClick={() => viewTopic(topic)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {topic.title}
                                                    </h6>
                                                    {topic.is_pinned && (
                                                        <span className="badge bg-warning text-dark me-1">
                                                            <i className="bx bx-pin"></i>
                                                        </span>
                                                    )}
                                                    {topic.is_locked && (
                                                        <span className="badge bg-secondary">
                                                            <i className="bx bx-lock"></i>
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-muted mb-2 small">
                                                    {topic.content.substring(0, 150)}...
                                                </p>
                                                <div className="d-flex align-items-center">
                                                    <small className="text-muted me-3">
                                                        <i className="bx bx-user me-1"></i>
                                                        {topic.author_name}
                                                    </small>
                                                    <small className="text-muted">
                                                        <i className="bx bx-time me-1"></i>
                                                        {formatRelativeTime(topic.created_at)}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 text-center">
                                        <div className="topic-stats">
                                            <div className="mb-1">
                                                <strong>{topic.reply_count}</strong>
                                                <br />
                                                <small className="text-muted">Replies</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-2 text-center">
                                        <div className="topic-stats">
                                            <div className="mb-1">
                                                <strong>{topic.view_count}</strong>
                                                <br />
                                                <small className="text-muted">Views</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {topic.tags && topic.tags.length > 0 && (
                                    <div className="mt-2">
                                        {topic.tags.map((tag, index) => (
                                            <span key={index} className="badge bg-light text-dark me-1">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                
                                {topic.last_reply_date && (
                                    <div className="mt-2 pt-2 border-top">
                                        <small className="text-muted">
                                            <strong>Last reply:</strong> {formatRelativeTime(topic.last_reply_date)} by {topic.last_reply_author}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-chat me-2 text-primary"></i>
                                Discussion Forum
                                {view === 'topics' && selectedCategory && (
                                    <span className="text-muted fs-6 ms-2">
                                        › {selectedCategory.name}
                                    </span>
                                )}
                            </h4>
                            <p className="text-muted mb-0">
                                {view === 'categories' 
                                    ? 'Professional discussions and knowledge sharing' 
                                    : 'Topics and discussions'
                                }
                            </p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {(categoriesLoading || topicsLoading) && (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading {view}...</div>
                </div>
            )}

            {/* Content */}
            {!categoriesLoading && !topicsLoading && (
                <>
                    {view === 'categories' && renderCategories()}
                    {view === 'topics' && renderTopics()}
                </>
            )}

            {/* No Results */}
            {!categoriesLoading && !topicsLoading && (
                <>
                    {view === 'categories' && categories.length === 0 && (
                        <div className="text-center py-5">
                            <i className="bx bx-chat display-4 text-muted"></i>
                            <h5 className="mt-3 text-muted">No forum categories found</h5>
                            <p className="text-muted">Forum categories will appear here when available.</p>
                        </div>
                    )}
                    
                    {view === 'topics' && filteredTopics.length === 0 && (
                        <div className="text-center py-5">
                            <i className="bx bx-message display-4 text-muted"></i>
                            <h5 className="mt-3 text-muted">No topics found</h5>
                            <p className="text-muted">
                                {searchTerm ? 'Try adjusting your search terms.' : 'Be the first to start a discussion!'}
                            </p>
                            {!searchTerm && (
                                <button 
                                    className="btn btn-primary"
                                    onClick={createNewTopic}
                                    disabled={!selectedCategory?.is_active}
                                >
                                    <i className="bx bx-plus me-1"></i>
                                    Create First Topic
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Infinite Scroll Indicator for Topics */}
            {view === 'topics' && !topicsLoading && hasMore && filteredTopics.length > 0 && (
                <div className="text-center py-3">
                    <small className="text-muted">Scroll down to load more topics</small>
                </div>
            )}

            <style>{`
                .forum-category-card {
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: 1px solid #e3e6f0;
                }
                
                .forum-category-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.15);
                }
                
                .forum-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                }
                
                .topic-card {
                    border: 1px solid #e3e6f0;
                    transition: transform 0.1s, box-shadow 0.1s;
                }
                
                .topic-card:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.1);
                }
                
                .topic-title {
                    color: #495057;
                    text-decoration: none;
                    transition: color 0.2s;
                }
                
                .topic-title:hover {
                    color: #007bff;
                    text-decoration: underline;
                }
                
                .topic-stats {
                    font-size: 0.875rem;
                }
                
                .badge {
                    font-size: 0.75rem;
                }
            `}</style>
        </div>
    );
};

export default ForumCenter;
